import { Injectable, NgZone } from '@angular/core';
import { environment } from '@environment';

import { GtmService } from '@core/services/analytics/gtm.service';
import { FeatureToggleService } from '@core/services/util/feature-toggle.service';
import { BehaviorSubject } from 'rxjs';

declare global {
    interface Window {
        fbq: any;
        lottie: any;
        Hls: any
    }
}

@Injectable({
    providedIn: 'root'
})
export class ThirdPartyScriptService {
    hlsSDKLoaded = new BehaviorSubject<any>(null);
    constructor(
        private ngZone: NgZone,
        private gtmService: GtmService,
        private featureToggleService: FeatureToggleService
    ) {}

    loadScriptsOnClient() {
        const scripts = ['tagManager', 'fbAnalytics', 'lottie', 'hls'];
        scripts.forEach(script => this._loadAllScript(script));
    }

    private _loadAllScript(scriptName: string) {
        this.ngZone.runOutsideAngular(() => {
            if (!this.featureToggleService.isEnabled(scriptName)) {
                return;
            }
            
            switch (scriptName) {
                case 'tagManager':
                    if (environment.NODE_ENV === 'production') {
                        this.loadGoogleTagManagerSdk();
                    }
                    break;

                case 'fbAnalytics':
                    this.loadFacebookAnalyticsSdk();
                    break;

                case 'lottie':
                    this.loadLottie();
                    break;

                case 'hls':
                    this.loadHLS();
                    break;

                default:
                    break;
            }
        });
    }

    private loadScript(src: string, onlaodCallback: () => void, onerrorCallback: (error: any) => void) {
        const script = document.createElement('script');
        script.src = src;
        script.defer = true;
        script.onload = onlaodCallback;
        script.onerror = onerrorCallback;

        const firstScript = document.getElementsByTagName('script')[0];
        firstScript.parentNode.insertBefore(script, firstScript);
    }

    private logScriptFailure(message: string) {
        this.gtmService.logWebAnalyticsEvent('script_failure', message);
    }

    loadGoogleTagManagerSdk() {
        window['dataLayer'] = window['dataLayer'] || [];
        window['dataLayer'].push({
            'gtm.start': new Date().getTime(),
            event: 'gtm.js'
        });

        const src = 'https://www.googletagmanager.com/gtm.js?id=' + environment.gtmKey;
        this.loadScript(
            src,
            () => this.gtmService.gtmSdkLoaded.emit(true),
            () => this.logScriptFailure('Failed To Load Google Tag Manager SDK')
        );
    }

    loadFacebookAnalyticsSdk() {
        if (window.fbq) return;
        window.fbq = function () {
            window.fbq.callMethod ? window.fbq.callMethod.apply(window.fbq, arguments) : window.fbq.queue.push(arguments);
        };
        window.fbq.queue = [];
        window.fbq.loaded = true;
        window.fbq.version = '2.0';

        const src = 'https://connect.facebook.net/en_US/fbevents.js';
        this.loadScript(
            src,
            () => {
                window.fbq('init', environment.fbAnalyticsKey);
                window.fbq('track', 'PageView');
            },
            () => this.logScriptFailure('Failed To Load Facebook Analytics SDK')
        );
    }

    loadLottie() {
        const src = 'https://unpkg.com/@dotlottie/player-component@2.7.12/dist/dotlottie-player.js';
        this.loadScript(
            src,
            () => {},
            () => this.logScriptFailure('Failed To Load Lottie Script')
        );
    }

    loadHLS() {
        const src = 'https://cdn.jsdelivr.net/npm/hls.js@latest/dist/hls.min.js';
        this.loadScript(
            src,
            () => { this.hlsSDKLoaded.next(true); },
            () => this.logScriptFailure('Failed To Load Hls Script')
        );
    }
}
